import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';

// Heart toggle for detail pages AND list cards. entityType is one of the
// plural route-style keys the favorites API canonicalizes on (`spells`,
// `feats`, `classes`, `races`, `monsters`, `items`, `skills`, `languages`,
// `deities`, `rulebooks`) — the same segment as the frontend URL for the
// content.
//
// State lives in FavoritesContext (one GET /api/favorites per login, shared
// by every heart) instead of a per-mount fetch. The favorites API is
// per-user AND per-system (req.systemId via the axios ?system= interceptor).
//
// Logged out it still renders (as an empty heart) and clicking routes to
// /login with state.from set so the user lands back on this page after
// signing in.
//
// The click handler calls preventDefault + stopPropagation so the button can
// sit inside a list-item <Link> without triggering the card's navigation (or
// its saveState/analytics onClick).
const FavoriteButton = ({ entityType, slug }) => {
  const { isAuthenticated } = useAuth();
  const { isFavorited, toggleFavorite, ensureLoaded } = useFavorites();
  // A heart on screen is what makes the favorites set worth fetching.
  useEffect(() => { if (isAuthenticated && ensureLoaded) ensureLoaded(); }, [isAuthenticated, ensureLoaded]);
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);

  if (!slug) return null;

  const favorited = isAuthenticated && isFavorited(entityType, slug);

  const handleClick = async (e) => {
    // May be nested in a list card's <Link> — never navigate, never bubble
    // to the anchor's onClick (saveState/track).
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `${location.pathname}${location.search}` } });
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      await toggleFavorite(entityType, slug);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex-shrink-0 p-1 mt-1 transition-transform duration-150 hover:scale-110"
      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={favorited}
      title={
        isAuthenticated
          ? (favorited ? 'Remove from favorites' : 'Add to favorites')
          : 'Log in to save favorites'
      }
    >
      <Heart
        className="w-5 h-5"
        fill={favorited ? 'currentColor' : 'none'}
        style={{ color: favorited ? '#C9A84C' : 'var(--text-muted)' }}
      />
    </button>
  );
};

export default FavoriteButton;
